"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useMemo, useRef, useState } from "react";

const SUPPORTED_LOCALES = ["en", "ar"] as const;

type LocaleCode = (typeof SUPPORTED_LOCALES)[number];

type NavItem = {
  key: "home" | "services" | "products" | "works" | "blog";
  sectionId: string;
};

const navItems: NavItem[] = [
  { key: "home", sectionId: "home" },
  { key: "services", sectionId: "services" },
  { key: "products", sectionId: "products" },
  { key: "works", sectionId: "works" },
  { key: "blog", sectionId: "blog" },
];

function getPathWithoutLocale(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0];

  if (first && SUPPORTED_LOCALES.includes(first as LocaleCode)) {
    const rest = segments.slice(1);
    return rest.length ? `/${rest.join("/")}` : "/";
  }

  return pathname || "/";
}

function withLocalePrefix(pathname: string, locale: string): string {
  const path = pathname.startsWith("/") ? pathname : `/${pathname}`;
  return path === "/" ? `/${locale}` : `/${locale}${path}`;
}

export default function GlobalHeader() {
  const t = useTranslations("Navigation");
  const locale = useLocale();
  const pathname = usePathname();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement | null>(null);

  const dir = locale === "ar" ? "rtl" : "ltr";
  const localHome = withLocalePrefix("/", locale);

  const currentPathWithoutLocale = useMemo(
    () => getPathWithoutLocale(pathname),
    [pathname],
  );
  /** The "book a consultation" CTA points at this page, so hide it here. */
  const isBookingPage = currentPathWithoutLocale.startsWith("/booking");
  const [activeSection, setActiveSection] = useState<string>("home");
  const sectionIds = useMemo(() => navItems.map((item) => item.sectionId), []);

  // Close the language menu on an outside click or Escape.
  useEffect(() => {
    if (!isLangOpen) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!langRef.current?.contains(event.target as Node)) setIsLangOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsLangOpen(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isLangOpen]);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const updateActiveSectionFromHash = () => {
      const hash = window.location.hash;
      const id = hash ? hash.slice(1) : "";

      if (id && sectionIds.includes(id)) {
        setActiveSection(id);
      }
    };

    updateActiveSectionFromHash();
    window.addEventListener("hashchange", updateActiveSectionFromHash);

    return () => {
      window.removeEventListener("hashchange", updateActiveSectionFromHash);
    };
  }, [pathname, sectionIds]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const sectionIdsToObserve = [...sectionIds, "contact"];
    const elements = sectionIdsToObserve
      .map((id) => document.getElementById(id))
      .filter((element): element is HTMLElement => Boolean(element));

    if (!elements.length) {
      return;
    }

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    const setSectionFromHash = () => {
      const hash = window.location.hash;
      const id = hash ? hash.slice(1) : "";
      if (id && sectionIds.includes(id)) {
        setActiveSection(id);
        return true;
      }
      return false;
    };

    const scrollToHash = () => {
      const hash = window.location.hash;
      if (!hash) {
        return;
      }

      const target = document.getElementById(hash.slice(1));
      if (!target) {
        return;
      }

      target.scrollIntoView({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "start",
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort(
            (a, b) => a.boundingClientRect.top - b.boundingClientRect.top,
          );

        if (visible.length > 0) {
          const newActive = visible[0].target.id;
          setActiveSection((current) =>
            current !== newActive ? newActive : current,
          );
        }
      },
      {
        root: null,
        rootMargin: "-45% 0px -45% 0px",
        threshold: [0.25, 0.5],
      },
    );

    elements.forEach((element) => observer.observe(element));

    const handleHashChange = () => {
      if (!setSectionFromHash()) {
        scrollToHash();
      }
    };

    setSectionFromHash();
    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);

    return () => {
      observer.disconnect();
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [sectionIds]);

  const handleLanguageChange = (nextLocale: string) => {
    if (nextLocale === locale) return;

    const nextPath = withLocalePrefix(currentPathWithoutLocale, nextLocale);
    const hash = typeof window !== "undefined" ? window.location.hash : "";

    setIsMenuOpen(false);
    // Release the scroll lock the mobile menu may have set, otherwise the new
    // page can load with `overflow: hidden` still on <body> and appear frozen.
    if (typeof document !== "undefined") {
      document.body.style.overflow = "";
    }

    // A full document load is required when switching locale: a client-side
    // push keeps the old locale's provider/messages mounted while the RSC
    // request is rewritten by the intl middleware, which left the UI hanging.
    window.location.assign(`${nextPath}${hash}`);
  };

  return (
    <header className="site-header" dir={dir}>
      <div className="site-header__inner">
        <div className="site-header__controls">
          {isBookingPage ? null : (
            <Link href={`/${locale}/booking`} className="site-header__cta">
              {t("cta")}
            </Link>
          )}

          {/* Custom menu — the native <select> rendered with the OS blue
              highlight and could not be styled (team notes, 15/10). */}
          <div className="site-header__language" ref={langRef}>
            <button
              type="button"
              onClick={() => setIsLangOpen((open) => !open)}
              aria-haspopup="listbox"
              aria-expanded={isLangOpen}
              aria-label={t("languageLabel")}
              className="site-header__language-trigger"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" className="site-header__globe">
                <path
                  fill="currentColor"
                  d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm6.93 6h-2.95a15.8 15.8 0 0 0-1.38-3.56A8.01 8.01 0 0 1 18.93 8zM12 4.04c.83 1.2 1.52 2.54 2.02 3.96H9.98A14.9 14.9 0 0 1 12 4.04zM4.26 14a8.08 8.08 0 0 1 0-4h3.25a17.3 17.3 0 0 0 0 4H4.26zm.81 2h2.95c.34 1.25.8 2.45 1.38 3.56A8.01 8.01 0 0 1 5.07 16zM8.02 14a15.4 15.4 0 0 1 0-4h7.96c.14.65.22 1.32.22 2s-.08 1.35-.22 2H8.02zm3.98 5.96A14.9 14.9 0 0 1 9.98 16h4.04A14.9 14.9 0 0 1 12 19.96zM14.6 19.56A15.8 15.8 0 0 0 15.98 16h2.95a8.01 8.01 0 0 1-4.33 3.56zM16.49 14a17.3 17.3 0 0 0 0-4h3.25a8.08 8.08 0 0 1 0 4h-3.25z"
                />
              </svg>
              <span>{locale === "ar" ? t("languageAr") : t("languageEn")}</span>
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
                className={`site-header__chevron ${isLangOpen ? "is-open" : ""}`}
              >
                <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {isLangOpen ? (
              <ul className="site-header__language-menu" role="listbox">
                {SUPPORTED_LOCALES.map((code) => {
                  const isCurrent = code === locale;

                  return (
                    <li key={code} role="none">
                      <button
                        type="button"
                        role="option"
                        aria-selected={isCurrent}
                        lang={code}
                        dir={code === "ar" ? "rtl" : "ltr"}
                        onClick={() => handleLanguageChange(code)}
                        className={`site-header__language-option ${isCurrent ? "is-current" : ""}`}
                      >
                        <span>{code === "ar" ? t("languageAr") : t("languageEn")}</span>
                        {isCurrent ? (
                          <svg viewBox="0 0 24 24" aria-hidden="true" className="size-4">
                            <path d="M5 13l4 4L19 7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        </div>

        <nav className="site-header__nav" aria-label={t("mainNavLabel")}>
          {navItems.map((item) => {
            // Home is the top of the page — linking to #home only litters
            // the URL with a hash (team notes, 15/10).
            const href =
              item.sectionId === "home" ? localHome : `${localHome}#${item.sectionId}`;
            const isActive = activeSection === item.sectionId;

            return (
              <Link
                key={item.key}
                href={href}
                className={`site-header__link ${isActive ? "is-active" : ""}`}
                onClick={() => setActiveSection(item.sectionId)}
              >
                {t(item.key)}
              </Link>
            );
          })}
        </nav>

        <Link href={localHome} className="site-header__brand" aria-label={t("logoAlt")}>
          <Image
            src="/genitech-icon.svg"
            alt={t("logoAlt")}
            width={148}
            height={52}
            priority
          />
        </Link>

        <button
          className="site-header__menu-button"
          type="button"
          onClick={() => setIsMenuOpen((prev) => !prev)}
          aria-expanded={isMenuOpen}
          aria-label={isMenuOpen ? t("closeMenu") : t("openMenu")}
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      <div className={`site-header__mobile-panel ${isMenuOpen ? "is-open" : ""}`}>
        <nav className="site-header__mobile-nav" aria-label={t("mainNavLabel")}>
          {navItems.map((item) => (
            <Link
              key={item.key}
              href={item.sectionId === "home" ? localHome : `${localHome}#${item.sectionId}`}
              className={`site-header__mobile-link ${activeSection === item.sectionId ? "is-active" : ""}`}
              onClick={() => {
                setActiveSection(item.sectionId);
                setIsMenuOpen(false);
              }}
            >
              {t(item.key)}
            </Link>
          ))}
          {isBookingPage ? null : (
            <Link
              href={`/${locale}/booking`}
              className="site-header__mobile-cta"
              onClick={() => setIsMenuOpen(false)}
            >
              {t("cta")}
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
}
